import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Calendar, Plus } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Loader } from '../components/common';
import { TaskCard } from '../components/tasks/TaskCard';
import { useTaskStore } from '../stores/taskStore';
import { useUIStore } from '../stores/uiStore';
import type { Task } from '../types';

export const UpcomingPage: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  // Use stable selectors to prevent re-renders from unstable references
  const tasks = useTaskStore((state) => state.tasks);
  const fetchTasks = useTaskStore((state) => state.fetchTasks);
  const setFilters = useTaskStore((state) => state.setFilters);
  const isLoading = useTaskStore((state) => state.isLoading);
  const { openTaskModal } = useUIStore();
  
  // Track if we've done the initial fetch for this route to prevent duplicates
  const initialFetchDone = useRef(false);
  const lastPath = useRef(location.pathname);

  // Fetch tasks due in the future (after today) - runs on mount and route change
  // Backend handles all filtering: dueDate >= tomorrow
  // Completed tasks remain visible for 24 hours
  useEffect(() => {
    // Only fetch if path changed (route navigation) or this is the first mount
    if (lastPath.current === location.pathname && initialFetchDone.current) {
      return;
    }
    
    lastPath.current = location.pathname;
    initialFetchDone.current = true;
    
    // Backend handles all filtering for upcoming view
    const pageFilters = { view: 'upcoming' };
    
    // Store filters in state so Header search can preserve them
    setFilters(pageFilters);
    
    // Fetch with the filters directly (don't rely on state which isn't updated yet)
    fetchTasks(pageFilters);
  }, [location.pathname]); // Minimal dependencies - only path change triggers fetch

  // Helper to format date label - MUST be defined before useMemo to avoid hoisting issues
  // (const variables are hoisted but not initialized, so calling this inside useMemo
  // before its definition would cause "formatDateLabel is not a function" error)
  const formatDateLabel = (dateKey: string, referenceDate: Date): string => {
    const date = new Date(dateKey + 'T00:00:00');
    const tomorrow = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate() + 1);
    const tomorrowKey = tomorrow.toISOString().split('T')[0];
    
    if (dateKey === tomorrowKey) {
      return t('upcoming.tomorrow');
    }
    
    // Format as "Mon, Jan 15" style
    const days = [t('upcoming.daySun'), t('upcoming.dayMon'), t('upcoming.dayTue'), t('upcoming.dayWed'), t('upcoming.dayThu'), t('upcoming.dayFri'), t('upcoming.daySat')];
    const months = [t('upcoming.monthJan'), t('upcoming.monthFeb'), t('upcoming.monthMar'), t('upcoming.monthApr'), t('upcoming.monthMay'), t('upcoming.monthJun'), t('upcoming.monthJul'), t('upcoming.monthAug'), t('upcoming.monthSep'), t('upcoming.monthOct'), t('upcoming.monthNov'), t('upcoming.monthDec')];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };
  // Separate tasks into pending and completed (within 24h)
  // Group by date for better organization
  const { groupedTasks, completedTasks } = useMemo(() => {
    const pendingByDate: Map<string, Task[]> = new Map();
    const completed: Task[] = [];
    
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowStart = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

    tasks.forEach(task => {
      if (task.completed) {
        // Check if completed within last 24 hours
        if (task.completedAt && new Date(task.completedAt) >= twentyFourHoursAgo) {
          completed.push(task);
        }
      } else if (task.dueDate) {
        const taskDueDate = new Date(task.dueDate);
        // Only include tasks due >= tomorrow
        if (taskDueDate >= tomorrowStart) {
          // Format date for grouping
          const dateKey = taskDueDate.toISOString().split('T')[0];
          if (!pendingByDate.has(dateKey)) {
            pendingByDate.set(dateKey, []);
          }
          pendingByDate.get(dateKey)!.push(task);
        }
      }
    });

    // Sort tasks within each date group by due date
    pendingByDate.forEach((dateTasks) => {
      dateTasks.sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return dateA - dateB;
      });
    });

    // Convert map to sorted array (by date, earliest first)
    const sortedDates = Array.from(pendingByDate.keys()).sort();
    const grouped = sortedDates.map(dateKey => ({
      date: dateKey,
      tasks: pendingByDate.get(dateKey) || [],
      label: formatDateLabel(dateKey, now)  // Pass now to avoid stale closure
    }));

    return { groupedTasks: grouped, completedTasks: completed };
  }, [tasks]); // formatDateLabel is stable (defined outside but used with explicit now param)



  const totalTasks = groupedTasks.reduce((sum, g) => sum + g.tasks.length, 0) + completedTasks.length;
  const hasNoTasks = totalTasks === 0;

  return (
    <div className="flex flex-col h-full">
      <Header title={t('upcoming.title')} showSearch />
      
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader size="md" />
          </div>
        ) : hasNoTasks ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
            <div className="text-gray-900 dark:text-gray-100 font-medium mb-2">
              {t('upcoming.emptyTitle')}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('upcoming.emptySubtitle')}
            </p>
            <button
              onClick={() => openTaskModal()}
              className="px-4 py-2 min-h-[44px] bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('upcoming.createTask')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Grouped by Date Sections */}
            {groupedTasks.map((group) => (
              <div key={group.date} className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {group.label} ({group.tasks.length})
                </h3>
                {group.tasks.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            ))}

            {/* Completed Tasks Section (within 24h) */}
            {completedTasks.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">
                  {t('upcoming.completedSection')} ({completedTasks.length})
                </h3>
                {completedTasks.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default UpcomingPage;